import { createSelector } from 'reselect'

import { getNotificationsForProjects } from '../utils/notification';

const getProjectMilestones = (milestones, projectId) => milestones.filter(milestone => milestone.project === projectId);

const getProjectEnd = (projectId, milestones) => Math.max(...getProjectMilestones(milestones, projectId).map(milestone => new Date(milestone.end).getTime()), 0) || Number.POSITIVE_INFINITY;
const getProjectStart = (projectId, milestones) => Math.min(...getProjectMilestones(milestones, projectId).map(milestone => new Date(milestone.start).getTime()), Number.POSITIVE_INFINITY) || 0;

const getProject = (projects, id) => projects.find(project => project.id === id);
export const makeGetProject = () => createSelector(getProject, project => project);

const getExistingActiveProjects = (projects, ids) => ids.filter(id => projects.some(project => !project.archived && !project.removed && project.id === id));
export const makeGetExistingActiveProjects = () => createSelector(getExistingActiveProjects, projects => projects);

const getDemoProject = projects => projects.find(project => project.demo);
export const makeGetDemoProject = () => createSelector(getDemoProject, project => project);

const getProjectsBySubteam = (projects, subteamId) => projects.filter(project => !project.removed && project.subteam === subteamId);
export const makeGetProjectsBySubteam = () => createSelector(getProjectsBySubteam, project => project);

const getSortedActiveProjectsBySubteam = (projects, notifications, subteamId) => getProjectsBySubteam(getSortedActiveProjects(projects, notifications), subteamId);
export const makeGetSortedActiveProjectsBySubteam = () => createSelector(getSortedActiveProjectsBySubteam, project => project);

const getSortedActiveProjects = (projects, notifications = [], excludedProjects = []) => projects
    .filter(project => !project.error && !project.loading && !project.removed && !project.archived && !excludedProjects.includes(project.id))
    .sort((p1, p2) => {
        if (!p1) return 1;
        if (!p2) return -1;

        const p1Notifications = !notifications.length ? 0 : getNotificationsForProjects(notifications, [p1.id]).length;
        const p2Notifications = !notifications.length ? 0 : getNotificationsForProjects(notifications, [p2.id]).length;
        const p1Name = (p1.name && p1.name.toLowerCase());
        const p2Name = (p2.name && p2.name.toLowerCase());

        if (p1Notifications > p2Notifications) return -1;
        if (p1Notifications < p2Notifications) return 1;

        if (p1Name > p2Name) return 1;
        if (p1Name < p2Name) return -1;

        return 0;
    });
export const makeGetSortedActiveProjects = () => createSelector(getSortedActiveProjects, projects => projects);

const getSortedActiveProjectsWithMilestonesEnabled = (projects, notifications = [], excludedProjects = []) => getSortedActiveProjects(projects, notifications, excludedProjects).filter(project => {
    return !project.disabled_modules || !project.disabled_modules.includes('milestones')
})
export const makeGetSortedActiveProjectsWithMilestonesEnabled = () => createSelector(getSortedActiveProjectsWithMilestonesEnabled, projects => projects);

const getActiveProjects = projects => projects.filter(project => !project.error && !project.loading && !project.archived);
export const makeGetActiveProjects = () => createSelector(getActiveProjects, projects => projects);

const getSortedByNameActiveProjects = projects  => projects
    .filter(project => project && !project.removed && !project.error && !project.loading && !project.archived)
    .sort((p1, p2) => {
        if (!p1) return 1;
        if (!p2) return -1;

        const p1Name = (p1.name && p1.name.toLowerCase());
        const p2Name = (p2.name && p2.name.toLowerCase());

        if (p1Name > p2Name) return 1;
        if (p1Name < p2Name) return -1;

        return 0;
    });
export const makeGetSortedByNameActiveProjects = () => createSelector(getSortedByNameActiveProjects, projects => projects);

const getSortedByNameArchivedProjects = projects  => projects
    .filter(project => project && !project.removed && !project.error && !project.loading && project.archived)
    .sort((p1, p2) => {
        if (!p1) return 1;
        if (!p2) return -1;

        const p1Name = (p1.name && p1.name.toLowerCase());
        const p2Name = (p2.name && p2.name.toLowerCase());

        if (p1Name > p2Name) return 1;
        if (p1Name < p2Name) return -1;

        return 0;
    });
export const makeGetSortedByNameArchivedProjects = () => createSelector(getSortedByNameArchivedProjects, projects => projects);

const getSortedByNameProjects = projects  => projects
    .filter(project => project && !project.removed && !project.error && !project.loading)
    .sort((p1, p2) => {
        if (!p1) return 1;
        if (!p2) return -1;

        const p1Name = (p1.name && p1.name.toLowerCase());
        const p2Name = (p2.name && p2.name.toLowerCase());

        if (p1Name > p2Name) return 1;
        if (p1Name < p2Name) return -1;

        return 0;
    });
export const makeGetSortedByNameProjects = () => createSelector(getSortedByNameProjects, projects => projects);

const getSortedByNameActiveUserProjects = (projects, userId) => projects
    .filter(project => !project.removed && !project.error && !project.loading && !project.archived && (project.owner === userId || (project.members || []).includes(userId)))
    .sort((p1, p2) => {
        if (!p1) return 1;
        if (!p2) return -1;

        const p1Name = (p1.name && p1.name.toLowerCase());
        const p2Name = (p2.name && p2.name.toLowerCase());

        if (p1Name > p2Name) return 1;
        if (p1Name < p2Name) return -1;

        return 0;
    });
export const makeGetSortedByNameActiveUserProjects = () => createSelector(getSortedByNameActiveUserProjects, projects => projects);

const getSortedByEndActiveProjects = (projects, milestones) => projects
    .filter(project => project && !project.removed && !project.error && !project.loading && !project.archived)
    .sort((p1, p2) => {
        if (!p1) return 1;
        if (!p2) return -1;

        const p1End = getProjectEnd(p1.id, milestones);
        const p2End = getProjectEnd(p2.id, milestones);
        const p1Name = (p1.name && p1.name.toLowerCase());
        const p2Name = (p2.name && p2.name.toLowerCase());

        if (p1End > p2End) return 1;
        if (p1End < p2End) return -1;

        if (p1Name > p2Name) return 1;
        if (p1Name < p2Name) return -1;

        return 0;
    });
export const makeGetSortedByEndActiveProjects = () => createSelector(getSortedByEndActiveProjects, projects => projects);

const getSortedByStartActiveProjects = (projects, milestones) => projects
    .filter(project => project && !project.removed && !project.error && !project.loading && !project.archived)
    .sort((p1, p2) => {
        if (!p1) return 1;
        if (!p2) return -1;

        const p1Start = getProjectStart(p1.id, milestones);
        const p2Start = getProjectStart(p2.id, milestones);
        const p1Name = (p1.name && p1.name.toLowerCase());
        const p2Name = (p2.name && p2.name.toLowerCase());

        if (p1Start > p2Start) return 1;
        if (p1Start < p2Start) return -1;

        if (p1Name > p2Name) return 1;
        if (p1Name < p2Name) return -1;

        return 0;
    });
export const makeGetSortedByStartActiveProjects = () => createSelector(getSortedByStartActiveProjects, projects => projects);

const getFilteredProjects = (projects, term) => projects.filter(project => project && !project.removed && project.name && project.name.toLowerCase().indexOf(term.toLowerCase()) > -1);
export const makeGetFilteredProjects = () => createSelector(getFilteredProjects, projects => projects);

const getUserActiveProjectsCount = (projects, userId) => projects
    .filter(project => !project.removed && !project.error && !project.loading && !project.archived && (project.owner === userId || (project.members || []).some(user => user.id === userId))).length;
export const makeGetUserActiveProjectsCount = () => createSelector(getUserActiveProjectsCount, projectsCount => projectsCount);
